import { useState, useContext, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { CampaignContext } from '../../context/campaign';
import { setCampaignInsurance } from '~/services/registration';
import {
    TextInput,
    CheckboxInput,
    currencyConfig,
} from '~/shared/components/form/Inputs';
import IntlCurrencyInput from 'react-intl-currency-input';
import { Button, Col, Row, Form, FormGroup, Input, Label } from 'reactstrap';
import { Loading } from '~/shared/components/Loading';

import { fetchMyCampaign } from '../../services/campaign';
import { yupResolver } from '@hookform/resolvers/yup';
import { validationSchema } from '~/shared/validation/InsuranceValidationSchema';
import toast from 'react-hot-toast';

const insuranceOptions = [
    {
        label: 'Yes, I have insurance',
        value: 'yes',
    },
    {
        label: 'No, I do not have insurance',
        value: 'no',
    },
];

const yesOptions = [
    {
        label: 'I have Medicare',
        value: 'ins_has_medicare',
    },
    {
        label: 'I have Medicaid',
        value: 'ins_has_medicaid',
    },
    {
        label: 'I have Private insurance',
        value: 'ins_has_private',
    },
    {
        label: 'I have Private insurance through COBRA',
        value: 'ins_has_cobra',
    },
];

const defaultValues = {
    ins_has_medicare: false,
    ins_has_medicaid: false,
    ins_has_private: false,
    ins_has_cobra: false,
    ins_comments: '',
    ins_company: '',
    ins_premium: null,
    ins_cobra_months: null,
    ins_cobra_amount: null,
    ins_has_none: false,
    ins_unknown: false,
    noCopay: false,
};

export const Insurance = ({}: any) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [readOnly, setReadOnly] = useState(false);

    const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
    const [insuranceOption, setInsuranceOption] = useState<string>('');
    const [noCopay, setNoCopay] = useState<boolean>(false);
    const {
        handleSubmit,
        watch,
        control,
        reset,
        setValue,
        getValues,
        trigger,
        formState: { errors, isSubmitting, isValid },
    } = useForm({
        mode: 'onBlur',
        defaultValues,
        resolver: yupResolver(validationSchema),
    });

    const history = useHistory();
    const location = useLocation<{ read_only?: boolean }>();

    const getCampaign = async () => {
        const resp = await fetchMyCampaign();

        if (resp.detail) {
            if (resp.detail === 'Invalid token.') {
                localStorage.removeItem('token');
                history.push('/login');
            }
        } else {
            campaignDispatch({
                type: 'set_campaign',
                payload: resp,
            });
        }
    };

    useEffect(() => {
        if (location.state) {
            const { read_only } = location.state;
            if (read_only) {
                setReadOnly(read_only);
            }
        }
    }, [location]);

    useEffect(() => {
        if (!campaign) {
            getCampaign();
        } else {
            const app = campaign.campaignApplication;
            reset(app);
            if (app.ins_has_none) {
                setInsuranceOption('no');
            } else if (app.ins_unknown) {
                setInsuranceOption('unknown');
            } else {
                setInsuranceOption('yes');
            }
            if (app?.ins_premium && parseFloat(app?.ins_premium) === 1) {
                setNoCopay(true);
            }
        }
    }, [campaign]);

    const onSubmit = async (data: any) => {
        try {
            toast.loading('Saving insurance information...');
            if (!data?.ins_premium) {
                data.ins_premium = 1;
            }
            const resp = await setCampaignInsurance(
                data,
                campaign?.campaignDetail?.campaign_id
            );

            toast.dismiss();

            if (resp.detail) {
                if (resp.detail === 'Invalid token.') {
                    localStorage.removeItem('token');
                    history.push('/login');
                    return;
                }
                toast.error(resp.detail);
                return;
            }

            campaignDispatch({
                type: 'set_campaign',
                payload: resp,
            });
            toast.success('Insurance information saved!');
            history.push('/dashboard');
        } catch (e) {
            console.error('Error saving insurance information:', e);
            toast.dismiss();
            toast.error('There was an error saving your insurance information');
        }
    };

    useEffect(() => {
        setValue('noCopay', noCopay, { shouldValidate: true });
        if (noCopay) {
            setValue('ins_premium', null);
        }
    }, [noCopay]);

    useEffect(() => {
        if (insuranceOption === 'no') {
            reset({ ...defaultValues, ins_has_none: true });
        } else if (insuranceOption === 'unknown') {
            reset({ ...defaultValues, ins_unknown: true });
        } else {
            setValue('ins_has_none', false);
        }
    }, [insuranceOption]);

    if (loading || !campaign) {
        return <Loading />;
    }

    const hasCobra = watch('ins_has_cobra');
    const hasMedicare = watch('ins_has_medicare');
    const hasMedicaid = watch('ins_has_medicaid');
    const hasPrivate = watch('ins_has_private');

    const requiresInsuranceCompany =
        hasCobra || hasMedicare || hasMedicaid || hasPrivate;

    const YesOptions = () => {
        return (
            <div>
                <Label className='mt-3'>Check all that apply*</Label>
                <FormGroup check className='mb-3'>
                    {yesOptions.map((opt, i) => {
                        return (
                            <CheckboxInput
                                key={i}
                                control={control}
                                id={opt.value}
                                name={opt.value}
                                label={opt.label}
                                value={true}
                            />
                        );
                    })}
                </FormGroup>
                {hasCobra && (
                    <>
                        <TextInput
                            name='ins_cobra_months'
                            label='Cobra Number of Months'
                            type='number'
                            control={control}
                            required={true}
                            ariaRequired={true}
                            readOnly={readOnly}
                            maxLength={3}
                        />
                        <FormGroup>
                            <Label>
                                Cobra Amount ${' '}
                                <span className='required'>*</span>
                            </Label>
                            <IntlCurrencyInput
                                onChange={(e, val, masked) => {
                                    if (readOnly) {
                                        return;
                                    }
                                    setValue('ins_cobra_amount', val);
                                }}
                                config={currencyConfig as any}
                                value={parseFloat(
                                    getValues('ins_cobra_amount') || '0'
                                )}
                                max={999999}
                                defaultValue={0}
                            />
                        </FormGroup>
                    </>
                )}
                <TextInput
                    label='Comments About Insurance'
                    type='textarea'
                    control={control}
                    name='ins_comments'
                    readOnly={readOnly}
                    maxLength={500}
                />
                <TextInput
                    label='Insurance Company'
                    control={control}
                    name='ins_company'
                    required={requiresInsuranceCompany}
                    maxLength={100}
                />
                <div>
                    <Label check>
                        <Input
                            type='checkbox'
                            checked={noCopay}
                            onChange={() => {
                                if (readOnly) {
                                    return;
                                }
                                setNoCopay(!noCopay);
                            }}
                            name='no_copay'
                        />
                        I do not have a Premium or Copay
                    </Label>
                </div>
                {!noCopay ? (
                    <FormGroup readOnly={readOnly}>
                        <Label>
                            Insurance Premium $ (Your monthly or annual
                            insurance payment)*
                        </Label>
                        <IntlCurrencyInput
                            className='form-control w-50'
                            onChange={(e, val, masked) => {
                                setValue('ins_premium', val);
                            }}
                            onBlur={() => trigger('ins_premium')}
                            config={currencyConfig as any}
                            value={parseFloat(getValues('ins_premium') || '0')}
                            max={999999}
                            defaultValue={0}
                        />
                        {errors.ins_premium && (
                            <div className='text-danger small mt-1'>
                                {errors.ins_premium.message as string}
                            </div>
                        )}
                    </FormGroup>
                ) : (
                    ''
                )}
            </div>
        );
    };

    const errorCount = Object.keys(errors).length;

    return (
        <div className='container__inner'>
            <Row className='min-vh-100'>
                <Col md='6'>
                    <div>
                        <h3>Insurance</h3>
                    </div>

                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <FormGroup check className='mb-3' readOnly={readOnly}>
                            {insuranceOptions.map((opt, i) => {
                                return (
                                    <div key={i}>
                                        <Label check>
                                            <Input
                                                type='radio'
                                                value={opt.value}
                                                onClick={() =>
                                                    setInsuranceOption(
                                                        opt.value
                                                    )
                                                }
                                                name='insurance_option'
                                                checked={
                                                    opt.value ===
                                                    insuranceOption
                                                }
                                            />
                                            {opt.label}
                                        </Label>
                                        {opt.value == 'yes' &&
                                            insuranceOption == 'yes' && (
                                                <YesOptions />
                                            )}
                                    </div>
                                );
                            })}
                        </FormGroup>
                    </Form>
                </Col>
            </Row>
            <div
                className='sticky-bottom p-4 bg-white'
                style={{
                    borderTop: '1px solid #0D0106',
                    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.25)',
                }}
            >
                <Button
                    color='outline-dark'
                    disabled={isSubmitting}
                    onClick={() => history.push('/patient/edit')}
                >
                    Cancel
                </Button>
                {!readOnly && (
                    <Button
                        color='primary'
                        onClick={() => handleSubmit(onSubmit)()}
                        disabled={
                            isSubmitting ||
                            !insuranceOption ||
                            readOnly ||
                            errorCount > 0
                        }
                    >
                        {isSubmitting ? `Saving...` : `Save`}
                    </Button>
                )}
            </div>
        </div>
    );
};
